import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowsClockwise } from "@phosphor-icons/react";

const DEFAULTS = { green_min: 90, amber_min: 50, down_amber_max: 150 };

export default function PerfThresholds() {
  const [form, setForm] = useState(DEFAULTS);
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/performance/settings")
      .then(({ data }) => { setForm(data.thresholds); setIsDefault(data.is_default); })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => Number(String(v).replace(",", "."));

  const save = async () => {
    const payload = {
      green_min: num(form.green_min),
      amber_min: num(form.amber_min),
      down_amber_max: num(form.down_amber_max),
    };
    if (Object.values(payload).some((v) => Number.isNaN(v))) return toast.error("Valeurs numériques obligatoires");
    if (!(payload.amber_min < payload.green_min)) return toast.error("Le seuil orange doit être inférieur au seuil vert");
    setSaving(true);
    try {
      const { data } = await apiClient.put("/performance/settings/thresholds", payload);
      setForm(data.thresholds); setIsDefault(false);
      toast.success("Seuils enregistrés — visibles par tous les collaborateurs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      const { data } = await apiClient.delete("/performance/settings/thresholds");
      setForm(data.thresholds); setIsDefault(true);
      toast.success("Seuils d'origine restaurés");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const g = num(form.green_min), a = num(form.amber_min), d = num(form.down_amber_max);

  return (
    <div className="max-w-3xl" data-testid="perf-thresholds">
      <div className="bg-white border border-zinc-200 p-5 sm:p-6">
        <h2 className="font-heading text-lg font-semibold tracking-tight">Seuils de couleur du tableau de bord</h2>
        <p className="text-xs text-zinc-500 mt-1">
          Le pourcentage est calculé sur « attendu à ce jour ». Ces seuils s'appliquent à tous les collaborateurs.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <div>
            <Label className="label-tiny text-zinc-500">Vert à partir de (%)</Label>
            <Input value={form.green_min} onChange={(e) => set("green_min", e.target.value)} inputMode="decimal"
              className="h-10 rounded-sm border-zinc-300 mt-1 tabular-nums" data-testid="threshold-green-input" />
          </div>
          <div>
            <Label className="label-tiny text-zinc-500">Orange à partir de (%)</Label>
            <Input value={form.amber_min} onChange={(e) => set("amber_min", e.target.value)} inputMode="decimal"
              className="h-10 rounded-sm border-zinc-300 mt-1 tabular-nums" data-testid="threshold-amber-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tiny text-zinc-500">Indicateurs « plus bas = mieux » — orange jusqu'à (%)</Label>
            <Input value={form.down_amber_max} onChange={(e) => set("down_amber_max", e.target.value)} inputMode="decimal"
              className="h-10 rounded-sm border-zinc-300 mt-1 tabular-nums" data-testid="threshold-down-input" />
            <p className="text-[11px] text-zinc-500 mt-1">
              Retours, litiges, SAV : vert tant que la valeur reste au niveau du seuil, orange jusqu'à ce dépassement, rouge au-delà.
            </p>
          </div>
        </div>

        <div className="mt-5 border border-zinc-200" data-testid="threshold-preview">
          <div className="label-tiny text-zinc-400 px-4 py-2 border-b border-zinc-100">Aperçu</div>
          <div className="divide-y divide-zinc-100">
            <Row color="bg-emerald-600" text="text-emerald-800" label="Objectif tenu" rule={`≥ ${g || "—"} % de l'attendu`} />
            <Row color="bg-amber-500" text="text-amber-800" label="À surveiller" rule={`${a || "—"} % – ${g || "—"} %`} />
            <Row color="bg-rose-600" text="text-rose-800" label="En alerte" rule={`< ${a || "—"} %`} />
            <Row color="bg-zinc-400" text="text-zinc-600" label="Bas = mieux" rule={`vert ≤ 100 % · orange ≤ ${d || "—"} % · rouge au-delà`} />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-5">
          <Button onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-9 text-xs" data-testid="threshold-save">
            {saving ? "Enregistrement…" : "Enregistrer les seuils"}
          </Button>
          <Button variant="outline" onClick={reset} disabled={isDefault} className="rounded-sm h-9 border-zinc-300 gap-2 text-xs" data-testid="threshold-reset">
            <ArrowsClockwise size={14} weight="bold" /> Seuils d'origine
          </Button>
          <span className="text-[11px] text-zinc-400">{isDefault ? "Seuils d'origine (90 / 50 %)" : "Seuils personnalisés"}</span>
        </div>
      </div>
    </div>
  );
}

const Row = ({ color, text, label, rule }) => (
  <div className="flex items-center gap-3 px-4 py-2.5">
    <span className={`w-3 h-3 ${color}`} />
    <span className={`text-sm font-medium ${text}`}>{label}</span>
    <span className="text-xs text-zinc-500 ml-auto tabular-nums">{rule}</span>
  </div>
);
